var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import PLAN_ORDER_COMPONENT_DELIVERIES from 'api/mutations/PLAN_ORDER_COMPONENT_DELIVERIES';
import SAVE_ORDER_COMPONENT_VARIANT_DELIVERIES from 'api/mutations/SAVE_ORDER_COMPONENT_VARIANT_DELIVERIES';
import GET_ORDER from 'api/queries/GET_ORDER';
import GET_ORDER_COMPONENT_VARIANT_STATS from 'api/queries/GET_ORDER_COMPONENT_VARIANT_STATS';
import GET_ORDER_SUPPLIERS from 'api/queries/GET_ORDER_SUPPLIERS';
import GET_ORDER_SUPPLIERS_PAGINATED from 'api/queries/GET_ORDER_SUPPLIERS_PAGINATED';
import { ORDER_SUPPLIERS_PAGE_SIZE } from 'constants/pagination';
import client from '../api/environment';
const initialState = {
    item: null,
    orderModels: [],
    orderSuppliers: [],
    orderSuppliersRaw: [],
    orderEvents: [],
    suppliersPageInfo: null,
    suppliersTotalCount: null,
    suppliersCurrentPage: 1,
    suppliersCursors: [''],
    selectedSupplierId: null,
    status: {
        fetch: 'idle',
        add: 'idle',
        remove: 'idle',
        other: 'idle',
        fetchOrderVolume: 'idle',
        fetchComponentVariantStats: 'idle',
        planDeliveries: 'idle',
        saveDeliveries: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        remove: null,
        other: null,
        fetchOrderVolume: null,
        fetchComponentVariantStats: null,
        planDeliveries: null,
        saveDeliveries: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        remove: false,
        other: false,
        fetchOrderVolume: false,
        fetchComponentVariantStats: false,
        planDeliveries: false,
        saveDeliveries: false,
    },
};
export const fetchOrder = createAsyncThunk('orderDetails/fetchOrder', (orderId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_ORDER,
            variables: {
                id: orderId,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchOrderSuppliers = createAsyncThunk('orderDetails/fetchOrderSuppliers', (orderId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_ORDER_SUPPLIERS,
            variables: {
                id: orderId,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.node.componentVariantsBySuppliers;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchOrderSuppliersPaginated = createAsyncThunk('orderDetails/fetchOrderSuppliersPaginated', ({ orderId, first = ORDER_SUPPLIERS_PAGE_SIZE, after = null, supplierId = null, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const variables = {
            id: orderId,
            first,
            after: after || null,
            supplierId: supplierId || null,
        };
        const response = yield client.query({
            query: GET_ORDER_SUPPLIERS_PAGINATED,
            variables,
            fetchPolicy: 'network-only',
        });
        return {
            edges: response.data.node.componentVariantsBySuppliersConnection.edges,
            pageInfo: response.data.node.componentVariantsBySuppliersConnection.pageInfo,
            totalCount: response.data.node.suppliersCount,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchOrderComponentVariantStats = createAsyncThunk('orderDetails/fetchOrderComponentVariantStats', (orderId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_ORDER_COMPONENT_VARIANT_STATS,
            variables: {
                id: orderId,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.node.componentVariantStats;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const planOrderComponentDeliveries = createAsyncThunk('orderDetails/planOrderComponentDeliveries', ({ orderComponentVariantId, plannedDeliveries, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.mutate({
            mutation: PLAN_ORDER_COMPONENT_DELIVERIES,
            variables: {
                orderComponentVariantId,
                plannedDeliveries,
            },
        });
        return response.data.planOrderComponentDeliveries.orderComponentVariant;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const saveOrderComponentVariantDeliveries = createAsyncThunk('orderDetails/saveOrderComponentVariantDeliveries', ({ orderComponentVariantId, deliveries, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.mutate({
            mutation: SAVE_ORDER_COMPONENT_VARIANT_DELIVERIES,
            variables: {
                orderComponentVariantId,
                deliveries,
            },
        });
        return response.data.saveOrderComponentVariantDeliveries.orderComponentVariant;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const goToSuppliersPage = createAsyncThunk('orderDetails/goToSuppliersPage', ({ pageNumber, supplierId = null }, { getState, dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const state = getState();
    const { suppliersCursors, item, suppliersTotalCount } = state.orderDetails;
    if (!item) {
        return rejectWithValue('Order not loaded');
    }
    if (pageNumber < 1) {
        return rejectWithValue('Invalid page number');
    }
    // Check if we have a total count and the requested page is beyond available pages
    if (suppliersTotalCount !== null && suppliersTotalCount !== undefined) {
        const totalPages = Math.ceil(suppliersTotalCount / ORDER_SUPPLIERS_PAGE_SIZE);
        if (pageNumber > totalPages) {
            return rejectWithValue('Invalid page number');
        }
    }
    const effectiveSupplierId = supplierId !== null ? supplierId : state.orderDetails.selectedSupplierId;
    // If we have a cursor for this page, use it directly
    if (pageNumber <= suppliersCursors.length) {
        const cursor = suppliersCursors[pageNumber - 1];
        const result = yield dispatch(fetchOrderSuppliersPaginated({
            orderId: item.id,
            first: ORDER_SUPPLIERS_PAGE_SIZE,
            after: cursor === '' ? null : cursor,
            supplierId: effectiveSupplierId,
        }));
        return result;
    }
    // We need to fetch pages sequentially until we reach the target page
    // Start from the last known cursor (which is the cursor for the last page we've loaded)
    let currentCursor = null;
    let currentPage = suppliersCursors.length;
    if (suppliersCursors.length > 0) {
        // Use the last cursor we have (this is the cursor to use to fetch the NEXT page)
        // If we're on page 1 (cursors.length = 1, cursors[0] = ''), we need to use the endCursor from pageInfo
        if (currentPage === 1 && ((_a = state.orderDetails.suppliersPageInfo) === null || _a === void 0 ? void 0 : _a.endCursor)) {
            currentCursor = state.orderDetails.suppliersPageInfo.endCursor;
        }
        else if (currentPage > 1) {
            // Use the endCursor from the last page we loaded
            currentCursor = suppliersCursors[suppliersCursors.length - 1];
            if (currentCursor === '') {
                currentCursor = null;
            }
        }
    }
    // Fetch pages sequentially until we reach the target page
    while (currentPage < pageNumber) {
        // eslint-disable-next-line no-await-in-loop
        const result = yield dispatch(fetchOrderSuppliersPaginated({
            orderId: item.id,
            first: ORDER_SUPPLIERS_PAGE_SIZE,
            after: currentCursor,
            supplierId: effectiveSupplierId,
        }));
        if (fetchOrderSuppliersPaginated.rejected.match(result)) {
            return rejectWithValue('Failed to fetch page');
        }
        // Update current cursor from the result
        const updatedState = getState();
        const updatedPageInfo = updatedState.orderDetails.suppliersPageInfo;
        // Check if there are more pages
        if (!(updatedPageInfo === null || updatedPageInfo === void 0 ? void 0 : updatedPageInfo.hasNextPage) || !(updatedPageInfo === null || updatedPageInfo === void 0 ? void 0 : updatedPageInfo.endCursor)) {
            // We've reached the last page
            break;
        }
        // Use the endCursor from this page to fetch the next page
        currentCursor = updatedPageInfo.endCursor;
        currentPage = updatedState.orderDetails.suppliersCurrentPage;
        // If we've reached or passed the target page, we're done
        if (currentPage >= pageNumber) {
            break;
        }
    }
    // Return the page we're on (should be the target page if everything worked)
    const finalState = getState();
    return {
        type: 'orderDetails/goToSuppliersPage/fulfilled',
        payload: finalState.orderDetails.suppliersCurrentPage,
    };
}));
export const goToNextSuppliersPage = createAsyncThunk('orderDetails/goToNextSuppliersPage', (_, { getState, dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { suppliersPageInfo, item, selectedSupplierId } = state.orderDetails;
    if (!item) {
        return rejectWithValue('Order not loaded');
    }
    if (!(suppliersPageInfo === null || suppliersPageInfo === void 0 ? void 0 : suppliersPageInfo.hasNextPage) || !suppliersPageInfo.endCursor) {
        return rejectWithValue('No more pages');
    }
    // Fetch the next page using the endCursor from current page
    return dispatch(fetchOrderSuppliersPaginated({
        orderId: item.id,
        first: ORDER_SUPPLIERS_PAGE_SIZE,
        after: suppliersPageInfo.endCursor,
        supplierId: selectedSupplierId,
    }));
}));
export const goToPreviousSuppliersPage = createAsyncThunk('orderDetails/goToPreviousSuppliersPage', (_, { getState, dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { suppliersCurrentPage, item, selectedSupplierId } = state.orderDetails;
    if (!item) {
        return rejectWithValue('Order not loaded');
    }
    if (suppliersCurrentPage <= 1) {
        return rejectWithValue('Already on first page');
    }
    return dispatch(goToSuppliersPage({ pageNumber: suppliersCurrentPage - 1, supplierId: selectedSupplierId }));
}));
const orderDetailsSlice = createSlice({
    name: 'orderDetails',
    initialState,
    reducers: {
        resetOrderDetailsState(state) {
            state.item = null;
            state.orderModels = [];
            state.orderSuppliers = [];
            state.orderSuppliersRaw = [];
            state.orderEvents = [];
            state.suppliersPageInfo = null;
            state.suppliersTotalCount = null;
            state.suppliersCurrentPage = 1;
            state.suppliersCursors = [''];
            state.status.fetch = 'idle';
            state.status.fetchOrderVolume = 'idle';
            state.status.fetchComponentVariantStats = 'idle';
            state.errors.fetch = null;
            state.refetchFlags.fetch = false;
            state.refetchFlags.fetchComponentVariantStats = false;
        },
        setOrderVolumeRefetch(state) {
            state.refetchFlags.fetchOrderVolume = true;
        },
        setOrderDetailsRefetchFlag(state, action) {
            state.refetchFlags.fetch = action.payload;
        },
        resetSuppliersPagination(state) {
            state.suppliersPageInfo = null;
            state.suppliersTotalCount = null;
            state.suppliersCurrentPage = 1;
            state.suppliersCursors = [''];
            state.orderSuppliers = [];
            state.orderSuppliersRaw = [];
            state.orderEvents = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrder.pending, (state) => {
            state.status.fetch = 'loading';
            // Reset stats status when fetching a new order so we know to fetch stats after order loads
            state.status.fetchComponentVariantStats = 'idle';
        })
            .addCase(fetchOrder.fulfilled, (state, action) => {
            const models = action.payload.models.edges.map((model) => model.node);
            state.status.fetch = 'succeeded';
            state.item = action.payload;
            state.orderModels = models;
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchOrder.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch =
                action.payload || 'Failed to fetch specification components';
            state.refetchFlags.fetch = false;
        })
            // fetchOrderSuppliers
            .addCase(fetchOrderSuppliers.pending, (state) => {
            state.status.fetchOrderVolume = 'loading';
        })
            .addCase(fetchOrderSuppliers.fulfilled, (state, action) => {
            const suppliers = action.payload.flatMap((supplier) => {
                // Calculate total ordered quantity for supplier
                const totalOrdered = supplier.componentVariants.reduce((sum, v) => sum + ((v === null || v === void 0 ? void 0 : v.calculatedConsumption) || 0), 0);
                // Calculate total delivered quantity for supplier (sum of COLLECTED deliveries with DONE status)
                const totalDelivered = supplier.componentVariants.reduce((sum, variant) => {
                    if (!variant.deliveries)
                        return sum;
                    const variantDelivered = variant.deliveries
                        .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                        .reduce((variantSum, d) => variantSum + (d.quantityCollected || 0), 0);
                    return sum + variantDelivered;
                }, 0);
                // Check if any variant has collected items or splits or is delayed
                const now = moment();
                const hasCollectedOrSplit = supplier.componentVariants.some((variant) => {
                    var _a, _b;
                    const hasCollectedDone = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE');
                    const hasPlanned = (_b = variant.deliveries) === null || _b === void 0 ? void 0 : _b.some((d) => d.deliveryType === 'PLANNED');
                    return hasCollectedDone || hasPlanned;
                });
                // Check if all variants are fully delivered (green condition)
                const allFullyDelivered = supplier.componentVariants.every((variant) => {
                    var _a;
                    const variantDelivered = variant.deliveries
                        ? variant.deliveries
                            .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                            .reduce((sum, d) => sum + (d.quantityCollected || 0), 0)
                        : 0;
                    const variantOrdered = (variant === null || variant === void 0 ? void 0 : variant.calculatedConsumption) || 0;
                    const hasPlanned = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'PLANNED');
                    return !hasPlanned && variantDelivered >= variantOrdered && variantOrdered > 0;
                });
                const hasMissingProductionEndDate = supplier.componentVariants.some((variant) => !(variant === null || variant === void 0 ? void 0 : variant.productionEndDate));
                const isDelayed = supplier.componentVariants.some((variant) => {
                    var _a;
                    const variantDelivered = variant.deliveries
                        ? variant.deliveries
                            .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                            .reduce((sum, d) => sum + (d.quantityCollected || 0), 0)
                        : 0;
                    const variantOrdered = (variant === null || variant === void 0 ? void 0 : variant.calculatedConsumption) || 0;
                    // Check planned deliveries delayed
                    const hasDelayedPlanned = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'PLANNED' &&
                        d.plannedDate &&
                        moment(d.plannedDate).isBefore(now));
                    if (hasDelayedPlanned)
                        return true;
                    // Check production delayed
                    if (variant.productionEndDate &&
                        moment(variant.productionEndDate).isBefore(now) &&
                        variantDelivered < variantOrdered) {
                        return true;
                    }
                    return false;
                });
                // Determine supplier status
                let supplierStatus = 'in-progress';
                if (hasMissingProductionEndDate) {
                    supplierStatus = 'error'; // Red when any variant missing production end date
                }
                else if (isDelayed) {
                    supplierStatus = 'error'; // Red when delayed
                }
                else if (allFullyDelivered && totalDelivered >= totalOrdered && totalOrdered > 0) {
                    supplierStatus = 'done'; // Green when all variants fully delivered (no PLANNED)
                }
                else if (hasCollectedOrSplit) {
                    supplierStatus = 'in-progress'; // Gray when has collected/split items
                }
                else {
                    supplierStatus = 'waiting'; // Blue when nothing delivered or partially delivered
                }
                const supplierRow = {
                    id: `${supplier.id}_supp`,
                    title: supplier.orderSupplier.supplier.shortName,
                    zam: totalOrdered,
                    dostawa: totalDelivered,
                    status: supplierStatus,
                    supplier: true,
                    supplierId: supplier.orderSupplier.supplier.id, // Store supplier ID for filtering
                };
                const variantRows = supplier.componentVariants.map((variant, index) => {
                    var _a, _b, _c;
                    // Calculate delivered quantity for this specific variant (only COLLECTED/DONE)
                    const variantDelivered = variant.deliveries
                        ? variant.deliveries
                            .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                            .reduce((sum, d) => sum + (d.quantityCollected || 0), 0)
                        : 0;
                    const variantOrdered = (variant === null || variant === void 0 ? void 0 : variant.calculatedConsumption) || 0;
                    // Check if production end date is missing (configuration error)
                    const missingProductionEndDate = !(variant === null || variant === void 0 ? void 0 : variant.productionEndDate);
                    const hasCollectedDone = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE');
                    const hasPlanned = (_b = variant.deliveries) === null || _b === void 0 ? void 0 : _b.some((d) => d.deliveryType === 'PLANNED');
                    // Check for delays
                    const now = moment();
                    let isDelayed = false;
                    // Check if planned delivery is delayed
                    if (hasPlanned) {
                        const delayedPlanned = (_c = variant.deliveries) === null || _c === void 0 ? void 0 : _c.some((d) => d.deliveryType === 'PLANNED' &&
                            d.plannedDate &&
                            moment(d.plannedDate).isBefore(now));
                        if (delayedPlanned)
                            isDelayed = true;
                    }
                    // Check if production is delayed and not fully delivered
                    if (variant.productionEndDate &&
                        moment(variant.productionEndDate).isBefore(now) &&
                        variantDelivered < variantOrdered) {
                        isDelayed = true;
                    }
                    // Determine variant status - prioritize PLANNED status over other conditions
                    let variantStatus = 'waiting';
                    if (missingProductionEndDate) {
                        variantStatus = 'error'; // Red when production end date not defined (configuration error)
                    }
                    else if (isDelayed) {
                        variantStatus = 'error'; // Red when delayed
                    }
                    else if (hasPlanned) {
                        variantStatus = 'in-progress'; // Blue when has PLANNED deliveries (including split)
                    }
                    else if (!hasPlanned && variantDelivered >= variantOrdered && variantOrdered > 0) {
                        variantStatus = 'done'; // Green when fully delivered (no PLANNED, all DONE)
                    }
                    else if (hasCollectedDone) {
                        variantStatus = 'in-progress'; // Blue when only COLLECTED/DONE exists
                    }
                    else {
                        variantStatus = 'waiting'; // Gray when nothing delivered (new order, no deliveries)
                    }
                    return {
                        id: `${supplier.id}_supp-variant-${index}`,
                        title: (variant === null || variant === void 0 ? void 0 : variant.name) || '-',
                        zam: variantOrdered,
                        dostawa: variantDelivered,
                        status: variantStatus,
                        statusTooltip: missingProductionEndDate
                            ? 'Data zakończenia produkcji nie została zdefiniowana dla komponentu'
                            : undefined,
                        supplier: false,
                        supplierId: supplier.orderSupplier.supplier.id, // Store supplier ID for filtering
                    };
                });
                return [supplierRow, ...variantRows];
            });
            // Generate timeline events from deliveries or production timeline
            const events = action.payload.flatMap((supplier) => {
                const supplierId = supplier.orderSupplier.supplier.id; // Store for filtering
                return supplier.componentVariants.flatMap((variant, index) => {
                    var _a, _b;
                    const metric = ((_b = (_a = variant.componentCategory) === null || _a === void 0 ? void 0 : _a.consumptionType) === null || _b === void 0 ? void 0 : _b.labelPL) || '';
                    const ordered = variant.calculatedConsumption || 0;
                    if (variant.deliveries && variant.deliveries.length > 0) {
                        // Separate PLANNED and COLLECTED deliveries
                        const plannedDeliveries = variant.deliveries.filter((d) => d.deliveryType === 'PLANNED');
                        // Only include COLLECTED deliveries with DONE status
                        const collectedDeliveries = variant.deliveries.filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE');
                        // Case 1: PLANNED deliveries exist
                        if (plannedDeliveries.length > 0) {
                            const now = moment();
                            // Create separate event for each PLANNED delivery
                            const plannedEvents = plannedDeliveries.flatMap((delivery, deliveryIndex) => {
                                // Use plannedDate if available, otherwise fall back to productionEndDate
                                const deliveryDate = delivery.plannedDate
                                    ? moment(delivery.plannedDate)
                                    : moment(variant.productionEndDate);
                                const eventStart = deliveryDate.clone().startOf('day');
                                const eventEnd = deliveryDate.clone().add(1, 'day').startOf('day');
                                // Determine status for this individual delivery
                                let status = 'waiting'; // Default: gray for fallback productionEndDate
                                let delayDays = 0;
                                // If user planned this delivery, show as in-progress (blue)
                                if (delivery.plannedDate) {
                                    status = 'in-progress';
                                    // But if planned date is in the past, show as error (red)
                                    if (moment(delivery.plannedDate).isBefore(now, 'day')) {
                                        status = 'error';
                                        delayDays = now.diff(moment(delivery.plannedDate), 'days');
                                    }
                                }
                                const mainEvent = {
                                    id: `planned-${variant.id}-${deliveryIndex}`,
                                    group: `${supplier.id}_supp-variant-${index}`,
                                    status,
                                    title: `${delivery.quantityPlanned} ${metric}`,
                                    simpleTitle: `${delivery.quantityPlanned} ${metric} - ${eventStart.format('DD.MM.YYYY')}`,
                                    supplier: false,
                                    supplierId,
                                    start_time: eventStart.valueOf(),
                                    end_time: eventEnd.valueOf(),
                                    canMove: false,
                                    canResize: false,
                                    canChangeGroup: false,
                                    componentVariantId: variant.id,
                                    hasDeliveries: true,
                                    quantity: delivery.quantityPlanned,
                                    ordered,
                                    metric,
                                    plannedDate: delivery.plannedDate || null,
                                    delayDays: delayDays > 0 ? delayDays : undefined,
                                };
                                return [mainEvent];
                            });
                            // Sub-case 1a: Has PLANNED deliveries + has collected items (DONE)
                            if (collectedDeliveries.length > 0) {
                                // Create separate events for COLLECTED/DONE
                                // Create separate event for each COLLECTED/DONE delivery (green)
                                if (collectedDeliveries.length > 0) {
                                    const collectedDoneEvents = collectedDeliveries.map((delivery, deliveryIndex) => {
                                        let deliveryDate;
                                        if (delivery.collectedDate) {
                                            deliveryDate = moment(delivery.collectedDate);
                                        }
                                        else if (variant.productionEndDate) {
                                            deliveryDate = moment(variant.productionEndDate);
                                        }
                                        else {
                                            deliveryDate = moment();
                                        }
                                        const eventStart = deliveryDate.clone().startOf('day');
                                        const eventEnd = deliveryDate.clone().add(1, 'day').startOf('day');
                                        return {
                                            id: `collected-done-${variant.id}-${deliveryIndex}`,
                                            group: `${supplier.id}_supp-variant-${index}`,
                                            status: 'done',
                                            title: `${delivery.quantityCollected || 0} ${metric}`,
                                            simpleTitle: `${delivery.quantityCollected || 0} ${metric} - ${eventStart.format('DD.MM.YYYY')}`,
                                            supplier: false,
                                            supplierId,
                                            start_time: eventStart.valueOf(),
                                            end_time: eventEnd.valueOf(),
                                            canMove: false,
                                            canResize: false,
                                            canChangeGroup: false,
                                            componentVariantId: variant.id,
                                            hasDeliveries: true,
                                            quantity: delivery.quantityCollected || 0,
                                            ordered,
                                            metric,
                                            collectedDate: delivery.collectedDate || null,
                                        };
                                    });
                                    plannedEvents.push(...collectedDoneEvents);
                                }
                                return plannedEvents;
                            }
                            // Sub-case 1b: Only planned deliveries (no collected yet)
                            return plannedEvents;
                        }
                        // Case 2: Only COLLECTED deliveries exist (no PLANNED)
                        if (collectedDeliveries.length > 0) {
                            const events = [];
                            // Create separate event for each COLLECTED/DONE delivery (green)
                            if (collectedDeliveries.length > 0) {
                                const collectedDoneEvents = collectedDeliveries.map((delivery, deliveryIndex) => {
                                    let deliveryDate;
                                    if (delivery.collectedDate) {
                                        deliveryDate = moment(delivery.collectedDate);
                                    }
                                    else if (variant.productionEndDate) {
                                        deliveryDate = moment(variant.productionEndDate);
                                    }
                                    else {
                                        deliveryDate = moment();
                                    }
                                    const eventStart = deliveryDate.clone().startOf('day');
                                    const eventEnd = deliveryDate.clone().add(1, 'day').startOf('day');
                                    return {
                                        id: `collected-done-${variant.id}-${deliveryIndex}`,
                                        group: `${supplier.id}_supp-variant-${index}`,
                                        status: 'done',
                                        title: `${delivery.quantityCollected || 0} ${metric}`,
                                        simpleTitle: `${delivery.quantityCollected || 0} ${metric} - ${eventStart.format('DD.MM.YYYY')}`,
                                        supplier: false,
                                        supplierId,
                                        start_time: eventStart.valueOf(),
                                        end_time: eventEnd.valueOf(),
                                        canMove: false,
                                        canResize: false,
                                        canChangeGroup: false,
                                        componentVariantId: variant.id,
                                        hasDeliveries: true,
                                        quantity: delivery.quantityCollected || 0,
                                        ordered,
                                        metric,
                                        collectedDate: delivery.collectedDate || null,
                                    };
                                });
                                events.push(...collectedDoneEvents);
                            }
                            return events;
                        }
                    }
                    // Case 3: No deliveries at all - show single day event on productionEndDate
                    if (variant.productionEndDate) {
                        const now = moment();
                        const eventDate = moment(variant.productionEndDate);
                        const eventStart = eventDate.clone().startOf('day');
                        const eventEnd = eventDate.clone().add(1, 'day').startOf('day');
                        // Check if production is delayed (no deliveries and past due date)
                        const isProductionDelayed = now.isAfter(eventDate, 'day') && ordered > 0;
                        let status = 'waiting';
                        if (isProductionDelayed) {
                            status = 'error'; // Red when delayed
                        }
                        else if (now.isAfter(eventDate, 'day')) {
                            status = 'done';
                        }
                        else if (now.isBetween(eventStart, eventEnd, 'day', '[]')) {
                            status = 'in-progress';
                        }
                        return [
                            {
                                id: `production-${variant.id}`,
                                group: `${supplier.id}_supp-variant-${index}`,
                                status,
                                title: `${ordered} ${metric}`,
                                supplier: false,
                                supplierId,
                                start_time: eventStart.valueOf(),
                                end_time: eventEnd.valueOf(),
                                canMove: false,
                                canResize: false,
                                canChangeGroup: false,
                                componentVariantId: variant.id,
                                hasDeliveries: false,
                                quantity: ordered,
                                metric,
                            },
                        ];
                    }
                    return [];
                });
            });
            state.status.fetchOrderVolume = 'succeeded';
            state.orderSuppliersRaw = action.payload; // Store raw API response for DeliveryPlanner
            state.orderSuppliers = suppliers;
            state.orderEvents = events;
            state.refetchFlags.fetch = false;
            state.refetchFlags.other = false;
            state.refetchFlags.fetchOrderVolume = false;
        })
            .addCase(fetchOrderSuppliers.rejected, (state, action) => {
            state.status.fetchOrderVolume = 'failed';
            state.errors.fetchOrderVolume =
                action.payload || 'Failed to fetch specification components';
            state.refetchFlags.fetch = false;
            state.refetchFlags.other = false;
            state.refetchFlags.fetchOrderVolume = false;
        })
            // fetchOrderSuppliersPaginated
            .addCase(fetchOrderSuppliersPaginated.pending, (state) => {
            state.status.fetchOrderVolume = 'loading';
        })
            .addCase(fetchOrderSuppliersPaginated.fulfilled, (state, action) => {
            const { edges, pageInfo, totalCount } = action.payload;
            const suppliers = edges.map((edge) => edge.node);
            // Store total count
            if (totalCount !== undefined && totalCount !== null) {
                state.suppliersTotalCount = totalCount;
            }
            // Process suppliers the same way as fetchOrderSuppliers
            const processedSuppliers = suppliers.flatMap((supplier) => {
                // ... same processing logic as fetchOrderSuppliers ...
                const totalOrdered = supplier.componentVariants.reduce((sum, v) => sum + ((v === null || v === void 0 ? void 0 : v.calculatedConsumption) || 0), 0);
                const totalDelivered = supplier.componentVariants.reduce((sum, variant) => {
                    if (!variant.deliveries)
                        return sum;
                    const variantDelivered = variant.deliveries
                        .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                        .reduce((variantSum, d) => variantSum + (d.quantityCollected || 0), 0);
                    return sum + variantDelivered;
                }, 0);
                const now = moment();
                const hasCollectedOrSplit = supplier.componentVariants.some((variant) => {
                    var _a, _b;
                    const hasCollectedDone = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE');
                    const hasPlanned = (_b = variant.deliveries) === null || _b === void 0 ? void 0 : _b.some((d) => d.deliveryType === 'PLANNED');
                    return hasCollectedDone || hasPlanned;
                });
                const allFullyDelivered = supplier.componentVariants.every((variant) => {
                    var _a;
                    const variantDelivered = variant.deliveries
                        ? variant.deliveries
                            .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                            .reduce((sum, d) => sum + (d.quantityCollected || 0), 0)
                        : 0;
                    const variantOrdered = (variant === null || variant === void 0 ? void 0 : variant.calculatedConsumption) || 0;
                    const hasPlanned = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'PLANNED');
                    return !hasPlanned && variantDelivered >= variantOrdered && variantOrdered > 0;
                });
                const hasMissingProductionEndDate = supplier.componentVariants.some((variant) => !(variant === null || variant === void 0 ? void 0 : variant.productionEndDate));
                const isDelayed = supplier.componentVariants.some((variant) => {
                    var _a;
                    const variantDelivered = variant.deliveries
                        ? variant.deliveries
                            .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                            .reduce((sum, d) => sum + (d.quantityCollected || 0), 0)
                        : 0;
                    const variantOrdered = (variant === null || variant === void 0 ? void 0 : variant.calculatedConsumption) || 0;
                    const hasDelayedPlanned = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'PLANNED' &&
                        d.plannedDate &&
                        moment(d.plannedDate).isBefore(now));
                    if (hasDelayedPlanned)
                        return true;
                    if (variant.productionEndDate &&
                        moment(variant.productionEndDate).isBefore(now) &&
                        variantDelivered < variantOrdered) {
                        return true;
                    }
                    return false;
                });
                let supplierStatus = 'in-progress';
                if (hasMissingProductionEndDate) {
                    supplierStatus = 'error';
                }
                else if (isDelayed) {
                    supplierStatus = 'error';
                }
                else if (allFullyDelivered && totalDelivered >= totalOrdered && totalOrdered > 0) {
                    supplierStatus = 'done';
                }
                else if (hasCollectedOrSplit) {
                    supplierStatus = 'in-progress';
                }
                else {
                    supplierStatus = 'waiting';
                }
                const supplierRow = {
                    id: `${supplier.id}_supp`,
                    title: supplier.orderSupplier.supplier.shortName,
                    zam: totalOrdered,
                    dostawa: totalDelivered,
                    status: supplierStatus,
                    supplier: true,
                    supplierId: supplier.orderSupplier.supplier.id, // Store supplier ID for filtering
                };
                const variantRows = supplier.componentVariants.map((variant, index) => {
                    var _a, _b, _c;
                    const variantDelivered = variant.deliveries
                        ? variant.deliveries
                            .filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE')
                            .reduce((sum, d) => sum + (d.quantityCollected || 0), 0)
                        : 0;
                    const variantOrdered = (variant === null || variant === void 0 ? void 0 : variant.calculatedConsumption) || 0;
                    const missingProductionEndDate = !(variant === null || variant === void 0 ? void 0 : variant.productionEndDate);
                    const hasCollectedDone = (_a = variant.deliveries) === null || _a === void 0 ? void 0 : _a.some((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE');
                    const hasPlanned = (_b = variant.deliveries) === null || _b === void 0 ? void 0 : _b.some((d) => d.deliveryType === 'PLANNED');
                    let isDelayed = false;
                    if (hasPlanned) {
                        const delayedPlanned = (_c = variant.deliveries) === null || _c === void 0 ? void 0 : _c.some((d) => d.deliveryType === 'PLANNED' &&
                            d.plannedDate &&
                            moment(d.plannedDate).isBefore(now));
                        if (delayedPlanned)
                            isDelayed = true;
                    }
                    if (variant.productionEndDate &&
                        moment(variant.productionEndDate).isBefore(now) &&
                        variantDelivered < variantOrdered) {
                        isDelayed = true;
                    }
                    let variantStatus = 'waiting';
                    // Determine variant status - prioritize PLANNED status over other conditions
                    if (missingProductionEndDate) {
                        variantStatus = 'error';
                    }
                    else if (isDelayed) {
                        variantStatus = 'error';
                    }
                    else if (hasPlanned) {
                        variantStatus = 'in-progress'; // Blue when has PLANNED deliveries
                    }
                    else if (!hasPlanned && variantDelivered >= variantOrdered && variantOrdered > 0) {
                        variantStatus = 'done';
                    }
                    else if (hasCollectedDone) {
                        variantStatus = 'in-progress';
                    }
                    else {
                        variantStatus = 'waiting';
                    }
                    return {
                        id: `${supplier.id}_supp-variant-${index}`,
                        title: (variant === null || variant === void 0 ? void 0 : variant.name) || '-',
                        zam: variantOrdered,
                        dostawa: variantDelivered,
                        status: variantStatus,
                        statusTooltip: missingProductionEndDate
                            ? 'Data zakończenia produkcji nie została zdefiniowana dla komponentu'
                            : undefined,
                        supplier: false,
                        supplierId: supplier.orderSupplier.supplier.id, // Store supplier ID for filtering
                    };
                });
                return [supplierRow, ...variantRows];
            });
            // Generate events (same logic as fetchOrderSuppliers)
            const events = suppliers.flatMap((supplier) => {
                const supplierId = supplier.orderSupplier.supplier.id; // Store for filtering
                return supplier.componentVariants.flatMap((variant, index) => {
                    var _a, _b;
                    const metric = ((_b = (_a = variant.componentCategory) === null || _a === void 0 ? void 0 : _a.consumptionType) === null || _b === void 0 ? void 0 : _b.labelPL) || '';
                    const ordered = variant.calculatedConsumption || 0;
                    if (variant.deliveries && variant.deliveries.length > 0) {
                        const plannedDeliveries = variant.deliveries.filter((d) => d.deliveryType === 'PLANNED');
                        const collectedDeliveries = variant.deliveries.filter((d) => d.deliveryType === 'COLLECTED' && d.status === 'DONE');
                        if (plannedDeliveries.length > 0) {
                            const now = moment();
                            const plannedEvents = plannedDeliveries.flatMap((delivery, deliveryIndex) => {
                                const deliveryDate = delivery.plannedDate
                                    ? moment(delivery.plannedDate)
                                    : moment(variant.productionEndDate);
                                const eventStart = deliveryDate.clone().startOf('day');
                                const eventEnd = deliveryDate.clone().add(1, 'day').startOf('day');
                                let status = 'waiting';
                                let delayDays = 0;
                                if (delivery.plannedDate) {
                                    status = 'in-progress';
                                    if (moment(delivery.plannedDate).isBefore(now, 'day')) {
                                        status = 'error';
                                        delayDays = now.diff(moment(delivery.plannedDate), 'days');
                                    }
                                }
                                const mainEvent = {
                                    id: `planned-${variant.id}-${deliveryIndex}`,
                                    group: `${supplier.id}_supp-variant-${index}`,
                                    status,
                                    title: `${delivery.quantityPlanned} ${metric}`,
                                    simpleTitle: `${delivery.quantityPlanned} ${metric} - ${eventStart.format('DD.MM.YYYY')}`,
                                    supplier: false,
                                    supplierId,
                                    start_time: eventStart.valueOf(),
                                    end_time: eventEnd.valueOf(),
                                    canMove: false,
                                    canResize: false,
                                    canChangeGroup: false,
                                    componentVariantId: variant.id,
                                    hasDeliveries: true,
                                    quantity: delivery.quantityPlanned,
                                    ordered,
                                    metric,
                                    plannedDate: delivery.plannedDate || null,
                                    delayDays: delayDays > 0 ? delayDays : undefined,
                                };
                                return [mainEvent];
                            });
                            if (collectedDeliveries.length > 0) {
                                const collectedDoneEvents = collectedDeliveries.map((delivery, deliveryIndex) => {
                                    let deliveryDate;
                                    if (delivery.collectedDate) {
                                        deliveryDate = moment(delivery.collectedDate);
                                    }
                                    else if (variant.productionEndDate) {
                                        deliveryDate = moment(variant.productionEndDate);
                                    }
                                    else {
                                        deliveryDate = moment();
                                    }
                                    const eventStart = deliveryDate.clone().startOf('day');
                                    const eventEnd = deliveryDate.clone().add(1, 'day').startOf('day');
                                    return {
                                        id: `collected-done-${variant.id}-${deliveryIndex}`,
                                        group: `${supplier.id}_supp-variant-${index}`,
                                        status: 'done',
                                        title: `${delivery.quantityCollected || 0} ${metric}`,
                                        simpleTitle: `${delivery.quantityCollected || 0} ${metric} - ${eventStart.format('DD.MM.YYYY')}`,
                                        supplier: false,
                                        supplierId,
                                        start_time: eventStart.valueOf(),
                                        end_time: eventEnd.valueOf(),
                                        canMove: false,
                                        canResize: false,
                                        canChangeGroup: false,
                                        componentVariantId: variant.id,
                                        hasDeliveries: true,
                                        quantity: delivery.quantityCollected || 0,
                                        ordered,
                                        metric,
                                        collectedDate: delivery.collectedDate || null,
                                    };
                                });
                                plannedEvents.push(...collectedDoneEvents);
                            }
                            return plannedEvents;
                        }
                        if (collectedDeliveries.length > 0) {
                            return collectedDeliveries.map((delivery, deliveryIndex) => {
                                let deliveryDate;
                                if (delivery.collectedDate) {
                                    deliveryDate = moment(delivery.collectedDate);
                                }
                                else if (variant.productionEndDate) {
                                    deliveryDate = moment(variant.productionEndDate);
                                }
                                else {
                                    deliveryDate = moment();
                                }
                                const eventStart = deliveryDate.clone().startOf('day');
                                const eventEnd = deliveryDate.clone().add(1, 'day').startOf('day');
                                return {
                                    id: `collected-done-${variant.id}-${deliveryIndex}`,
                                    group: `${supplier.id}_supp-variant-${index}`,
                                    status: 'done',
                                    title: `${delivery.quantityCollected || 0} ${metric}`,
                                    simpleTitle: `${delivery.quantityCollected || 0} ${metric} - ${eventStart.format('DD.MM.YYYY')}`,
                                    supplier: false,
                                    supplierId,
                                    start_time: eventStart.valueOf(),
                                    end_time: eventEnd.valueOf(),
                                    canMove: false,
                                    canResize: false,
                                    canChangeGroup: false,
                                    componentVariantId: variant.id,
                                    hasDeliveries: true,
                                    quantity: delivery.quantityCollected || 0,
                                    ordered,
                                    metric,
                                    collectedDate: delivery.collectedDate || null,
                                };
                            });
                        }
                    }
                    if (variant.productionEndDate) {
                        const now = moment();
                        const eventDate = moment(variant.productionEndDate);
                        const eventStart = eventDate.clone().startOf('day');
                        const eventEnd = eventDate.clone().add(1, 'day').startOf('day');
                        const isProductionDelayed = now.isAfter(eventDate, 'day') && ordered > 0;
                        let status = 'waiting';
                        if (isProductionDelayed) {
                            status = 'error';
                        }
                        else if (now.isAfter(eventDate, 'day')) {
                            status = 'done';
                        }
                        else if (now.isBetween(eventStart, eventEnd, 'day', '[]')) {
                            status = 'in-progress';
                        }
                        return [
                            {
                                id: `production-${variant.id}`,
                                group: `${supplier.id}_supp-variant-${index}`,
                                status,
                                title: `${ordered} ${metric}`,
                                supplier: false,
                                start_time: eventStart.valueOf(),
                                end_time: eventEnd.valueOf(),
                                canMove: false,
                                canResize: false,
                                canChangeGroup: false,
                                componentVariantId: variant.id,
                                hasDeliveries: false,
                                quantity: ordered,
                                metric,
                            },
                        ];
                    }
                    return [];
                });
            });
            // Always replace suppliers (pagination replaces, doesn't append)
            state.orderSuppliers = processedSuppliers;
            state.orderEvents = events;
            state.orderSuppliersRaw = suppliers;
            state.suppliersPageInfo = {
                hasNextPage: pageInfo.hasNextPage,
                endCursor: pageInfo.endCursor,
                startCursor: pageInfo.startCursor,
            };
            // Track cursors for page navigation
            const { after } = action.meta.arg;
            const afterCursor = after || '';
            if (afterCursor === '') {
                // Loading page 1 - reset cursors
                state.suppliersCurrentPage = 1;
                if (state.suppliersCursors.length === 0 || state.suppliersCursors[0] !== '') {
                    state.suppliersCursors = [''];
                }
            }
            else {
                // Loading a subsequent page
                // cursors[0] is for page 1 (empty string), cursors[1] is for page 2, etc.
                const cursorIndex = state.suppliersCursors.indexOf(afterCursor);
                if (cursorIndex >= 0) {
                    // We have this cursor, so we know which page this is
                    // cursorIndex 0 = page 1, cursorIndex 1 = page 2, so page = cursorIndex + 1
                    state.suppliersCurrentPage = cursorIndex + 1;
                }
                else {
                    // New cursor - add it to the array
                    state.suppliersCursors.push(afterCursor);
                    state.suppliersCurrentPage = state.suppliersCursors.length;
                }
            }
            // Add endCursor for next page only if there actually is a next page
            // Only add if we're on the last known page and there's a next page available
            if (pageInfo.hasNextPage &&
                pageInfo.endCursor &&
                !state.suppliersCursors.includes(pageInfo.endCursor) &&
                state.suppliersCurrentPage === state.suppliersCursors.length) {
                state.suppliersCursors.push(pageInfo.endCursor);
            }
            state.status.fetchOrderVolume = 'succeeded';
            state.refetchFlags.fetch = false;
            state.refetchFlags.other = false;
            state.refetchFlags.fetchOrderVolume = false;
        })
            .addCase(fetchOrderSuppliersPaginated.rejected, (state, action) => {
            state.status.fetchOrderVolume = 'failed';
            state.errors.fetchOrderVolume =
                action.payload || 'Failed to fetch order suppliers';
            state.refetchFlags.fetch = false;
            state.refetchFlags.other = false;
            state.refetchFlags.fetchOrderVolume = false;
        })
            // fetchOrderComponentVariantStats
            .addCase(fetchOrderComponentVariantStats.pending, (state) => {
            state.status.fetchComponentVariantStats = 'loading';
        })
            .addCase(fetchOrderComponentVariantStats.fulfilled, (state, action) => {
            state.status.fetchComponentVariantStats = 'succeeded';
            if (!state.item) {
                state.item = { componentVariantStats: action.payload };
            }
            else {
                state.item = Object.assign(Object.assign({}, state.item), { componentVariantStats: action.payload });
            }
            state.refetchFlags.fetchComponentVariantStats = false;
        })
            .addCase(fetchOrderComponentVariantStats.rejected, (state, action) => {
            state.status.fetchComponentVariantStats = 'failed';
            state.errors.fetchComponentVariantStats =
                action.payload || 'Failed to fetch component variant stats';
            state.refetchFlags.fetchComponentVariantStats = false;
        })
            // planOrderComponentDeliveries
            .addCase(planOrderComponentDeliveries.pending, (state) => {
            state.status.planDeliveries = 'loading';
        })
            .addCase(planOrderComponentDeliveries.fulfilled, (state) => {
            state.status.planDeliveries = 'succeeded';
            state.refetchFlags.fetchOrderVolume = true;
            state.refetchFlags.fetchComponentVariantStats = true; // Refetch only stats, not full order
            state.refetchFlags.planDeliveries = false;
        })
            .addCase(planOrderComponentDeliveries.rejected, (state, action) => {
            state.status.planDeliveries = 'failed';
            state.errors.planDeliveries = action.payload || 'Failed to plan deliveries';
            state.refetchFlags.planDeliveries = false;
        })
            // saveOrderComponentVariantDeliveries
            .addCase(saveOrderComponentVariantDeliveries.pending, (state) => {
            state.status.saveDeliveries = 'loading';
        })
            .addCase(saveOrderComponentVariantDeliveries.fulfilled, (state) => {
            state.status.saveDeliveries = 'succeeded';
            state.refetchFlags.fetchOrderVolume = true;
            state.refetchFlags.fetchComponentVariantStats = true; // Refetch only stats, not full order
            state.refetchFlags.saveDeliveries = false;
        })
            .addCase(saveOrderComponentVariantDeliveries.rejected, (state, action) => {
            state.status.saveDeliveries = 'failed';
            state.errors.saveDeliveries = action.payload || 'Failed to save deliveries';
            state.refetchFlags.saveDeliveries = false;
        });
    },
});
export const { resetOrderDetailsState, setOrderVolumeRefetch, setOrderDetailsRefetchFlag, resetSuppliersPagination, } = orderDetailsSlice.actions;
export const selectOrderDetailsStatus = (state) => state.orderDetails.status;
export const selectOrderDetailsErrors = (state) => state.orderDetails.errors;
export const selectOrderDetailsRefetchFlags = (state) => state.orderDetails.refetchFlags;
export const selectOrderDetailsSuppliersRaw = (state) => state.orderDetails.orderSuppliersRaw;
export const selectOrderDetailsItem = (state) => state.orderDetails.item;
export const selectOrderDetailsSuppliers = (state) => state.orderDetails.orderSuppliers;
export const selectOrderDetailsEvents = (state) => state.orderDetails.orderEvents;
export const selectOrderDetailsModelsAssigned = (state) => state.orderDetails.orderModels;
export const selectOrderDetailsSuppliersPageInfo = (state) => state.orderDetails.suppliersPageInfo;
export const selectOrderDetailsSuppliersCurrentPage = (state) => state.orderDetails.suppliersCurrentPage;
export const selectOrderDetailsSuppliersCursors = (state) => state.orderDetails.suppliersCursors;
export const selectOrderDetailsSuppliersTotalCount = (state) => state.orderDetails.suppliersTotalCount;
export const selectModelAssignedId = (id) => createSelector((state) => state.orderDetails.orderModels, (orderModels) => orderModels.find((model) => model.modelSpecificationNode.id === id));
export const selectModelById = (modelId) => createSelector((state) => state.orderDetails.orderModels || [], (orderModels) => orderModels.find((model) => String(model.id) === String(modelId)) || null);
export default orderDetailsSlice.reducer;
